import { useState, useEffect, useContext } from "react";
import { Button } from "@/components/ui/button";
import { Briefcase, Menu, X } from "lucide-react";
import { CalendlyContext } from "@/App";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { setIsOpen } = useContext(CalendlyContext);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        scrolled ? "shadow-md py-3" : "py-5"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          <a href="#" className="flex items-center gap-0.5">
            <img 
              src="/logos/headerlogowithname.png" 
              alt="Advensify Logo" 
              className="h-10 w-auto"
            />
         
          </a>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#home" className="navbar-link active">Home</a>
            <a href="#services" className="navbar-link">Services</a>
            <a href="#why-us" className="navbar-link">Why Us</a>
            <a href="#clients" className="navbar-link">Clients</a>
            <a href="#about" className="navbar-link">About</a>
            <a href="#contact" className="navbar-link">Contact</a>
          </nav>
          
          <div className="hidden md:flex items-center space-x-4">
            <Button
              variant="outline"
              className="text-emerald-500 hover:bg-emerald-500 hover:text-white hover:border-emerald-500 transition-all duration-300"
              onClick={() => scrollToSection('services')}
            >
              Explore Our Services
            </Button>
            <Button 
              className="bg-emerald-500 hover:bg-emerald-600 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              onClick={() => setIsOpen(true)}
            >
              <Briefcase className="mr-2 h-4 w-4" />
              Get a Free Consultation
            </Button>
            <a 
              href="https://wa.me/message/IBBGYRZSI7HXP1" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-500 text-white hover:bg-emerald-600 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              aria-label="WhatsApp"
              title="Let's Connect on WhatsApp"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </a>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-emerald-500" />
            ) : (
              <Menu className="h-6 w-6 text-emerald-500" />
            )}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 animate-fade-in">
            <nav className="flex flex-col space-y-4">
              <a href="#home" className="navbar-link active">Home</a>
              <a href="#services" className="navbar-link">Services</a>
              <a href="#why-us" className="navbar-link">Why Us</a>
              <a href="#clients" className="navbar-link">Clients</a>
              <a href="#about" className="navbar-link">About</a>
              <a href="#contact" className="navbar-link">Contact</a>
            </nav>
            <div className="mt-6 flex flex-col space-y-3">
              <Button 
                variant="outline"
                className="w-full text-emerald-700 border-emerald-700 hover:bg-emerald-700 hover:text-white transition-all duration-300"
                onClick={() => scrollToSection('services')}
              >
                Explore Our Services
              </Button>
              <Button 
                className="w-full bg-emerald-500 hover:bg-emerald-600"
                onClick={() => setIsOpen(true)}
              >
                <Briefcase className="mr-2 h-4 w-4" />
                Get a Free Consultation
              </Button>
              <a 
                href="https://wa.me/message/IBBGYRZSI7HXP1" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center justify-center w-full py-2 rounded-md bg-emerald-500 text-white hover:bg-emerald-600"
                aria-label="WhatsApp"
                title="Let's Connect on WhatsApp"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Let's Connect on WhatsApp
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
